import { cn } from "../lib/cn";

// Variant recipes follow the Product Styleguide's "05 · Buttons & Controls"
// specimens (Figma dR8cJ6hRSW5eERToo64boe, node 34:28479): 8px-radius
// rectangles, Outfit Bold/SemiBold 15px, px-[24px] py-[16px] at the base size.
// The styleguide's "Primary" specimen is the Deep Navy fill and its ember fill
// is labeled "Enrollment CTA"; the code keeps the established variant names
// (`primary` = ember, `secondary` = navy) so call sites stay stable.
export type ButtonVariant = "primary" | "secondary" | "outlined" | "soft" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-ember font-bold text-white hover:opacity-90 active:scale-[0.97] disabled:bg-muted disabled:text-cloud-blue",
  secondary:
    "bg-deep-navy font-bold text-white hover:opacity-80 active:scale-[0.97] disabled:bg-muted disabled:text-cloud-blue",
  outlined:
    "border-2 border-ember bg-transparent font-bold text-ember hover:bg-ember/7 active:scale-[0.97] active:bg-ember/14 disabled:border-muted disabled:text-cloud-blue",
  soft: "bg-soft font-semibold text-midnight hover:bg-soft-hover active:scale-[0.97] active:bg-soft-active disabled:bg-muted disabled:text-cloud-blue",
  ghost:
    "bg-transparent font-semibold text-ember hover:opacity-70 active:opacity-50 disabled:text-cloud-blue",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-4 py-2 text-[13px]",
  md: "px-6 py-4 text-[15px]",
  lg: "px-8 py-4 text-lg",
};

/**
 * The className for a button-shaped control at a variant and size, the single
 * source of truth the shared `Button` renders and a navigating `<Link>` reuses
 * so it matches a real button pixel for pixel. Callers append their own layout
 * classes (icon gap, width) last, letting `cn` resolve conflicts.
 */
export function buttonClasses(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  extra?: string,
): string {
  return cn(
    "inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-lg leading-[1.5] transition",
    "disabled:cursor-not-allowed",
    variantStyles[variant],
    sizeStyles[size],
    extra,
  );
}
